import { Checkbox, Chip, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from "@mui/material";
import { styled } from "@mui/material/styles";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { assetTypes } from "../types/assetTypes";
import { clientTypes } from "../types/clientTypes";
import { engineeringTeamTypes } from "../types/engineeringTeamTypes";
import { marketRegions } from "../types/marketRegions";
import { propertyManagementTypes } from "../types/propertyManagementTypes";
import { isEmptyStr } from "../utils/operations";

// Styled components

const StyledLabelSpacing = styled("div")({
    marginTop: "8px",
});

const StyledTypeChipContainer = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    padding: 0,
});

const StyledPortfolioPropertyContainer = styled("div")({
    overflow: "auto",
    maxHeight: "485px",
    paddingRight: "20px",
});

const StyledPortfolioPropertyLinkContainer = styled("div")({
    paddingBottom: "15px",
    paddingTop: "15px",
    borderBottom: "1px solid #E0E0E0",
    textAlign: "right",
});

const StyledPortfolioPropertyLink = styled(Link)({
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: "12px",
    fontWeight: "bold",
    textDecoration: "none",
});

const StyledPortfolioPropertyAddress = styled("span")({
    color: "#E0E0E0",
    fontSize: "12px",
    fontWeight: "normal",
});

// New styled components for grid replacement
const StyledMasterPropertyContainer = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    maxHeight: "810px",
    gap: "1vw",
});

const StyledFlexItemRow = styled("div")({
    display: "flex",
    gap: "8px",
});

const MasterPropertyForm = (props) => {
    const { data, disableInputs, errors, handleInput, hasPortfolioProperties, isSnapshot } = props;

    const initializeSelectedTaxType = () => {
        let selectedTaxType = null;

        if (!isEmpty(data?.w9Ssn)) {
            selectedTaxType = "SSN";
        } else if (!isEmpty(data?.w9TaxId)) {
            selectedTaxType = "W9";
        }
        return selectedTaxType;
    };

    const [selectedTaxIdType, setSelectedTaxIdType] = useState(initializeSelectedTaxType);

    const handleTaxIdSelection = (e) => {
        if (e.target.value === selectedTaxIdType) {
            setSelectedTaxIdType(null);
            handleInput("w9Ssn", "");
            handleInput("w9TaxId", "");
        } else if (e.target.value === "W9") {
            setSelectedTaxIdType("W9");
            handleInput("w9Ssn", "");
        } else if (e.target.value === "SSN") {
            setSelectedTaxIdType("SSN");
            handleInput("w9TaxId", "");
        }
    };

    const removeChip = (key, chipArr, type) => {
        let filteredChips = chipArr
            .filter((value) => {
                return value !== type;
            })
            .toString();
        let updatedChips = isEmptyStr(filteredChips) ? null : filteredChips;
        handleInput(key, updatedChips);
    };

    return (
        <React.Fragment>
            <StyledMasterPropertyContainer>
                <div style={{ flex: "4 4 0" }}>
                    <StyledFlexItemRow>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!data.ownersAssociationFlg}
                                    onChange={(e, checked) => handleInput(e.target.value, checked)}
                                    value="ownersAssociationFlg"
                                    disabled={disableInputs}
                                    color="primary"
                                />
                            }
                            label="Owners Association"
                        />
                        <BSInput
                            id="landAcreage"
                            label="Land Acreage"
                            placeholder="Enter land acreage"
                            value={data?.landAcreage ?? ""}
                            error={errors?.landAcreage ?? null}
                            errorText={"Field must be a positive number"}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={!data.ownersAssociationFlg || isSnapshot}
                        />
                        <BSInput
                            id="oaMemberCount"
                            label="Owners Association Member Count"
                            placeholder="Enter OA member count"
                            value={data?.oaMemberCount ?? ""}
                            error={errors?.oaMemberCount ?? null}
                            errorText={"Field must be a positive number"}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={!data.ownersAssociationFlg || isSnapshot}
                        />
                    </StyledFlexItemRow>

                    <StyledFlexItemRow>
                        <BSInput
                            id="accountingSoftwareId"
                            label="Accounting Software System Id"
                            placeholder="Enter ID"
                            value={data?.accountingSoftwareId ?? ""}
                            error={errors?.accountingSoftwareId ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={disableInputs}
                        />
                        <BSInput
                            id="portfolioNumber"
                            label="Portfolio Number"
                            placeholder="Enter portfolio number"
                            value={data?.portfolioNumber ?? ""}
                            error={errors?.portfolioNumber ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={disableInputs}
                        />
                    </StyledFlexItemRow>

                    <StyledFlexItemRow>
                        <BSSelect
                            id="marketRegion"
                            label="Market Region"
                            placeholder="Select market region"
                            value={data?.marketRegion ?? ""}
                            error={errors?.marketRegion ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            extraFormControlStyles={{ flex: "1 1 0" }}
                            options={[
                                {
                                    value: "",
                                    label: "Select",
                                    disabled: true,
                                },
                                ...marketRegions,
                            ]}
                            disabled={disableInputs}
                        />
                        <BSInput
                            id="buildingName"
                            label="Building Name"
                            placeholder="Enter building name"
                            value={data?.buildingName ?? ""}
                            error={errors?.buildingName ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            extraFormControlStyles={{ flex: "1 1 0" }}
                            disabled={disableInputs}
                        />
                    </StyledFlexItemRow>

                    <StyledFlexItemRow>
                        <BSSelect
                            id="engineeringTeam"
                            label="Engineering Team"
                            placeholder="Select engineering team"
                            value={data?.engineeringTeam ?? ""}
                            error={errors?.engineeringTeam ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            extraFormControlStyles={{ flex: "1 1 0" }}
                            options={[
                                {
                                    value: "",
                                    label: "Select",
                                    disabled: true,
                                },
                                ...engineeringTeamTypes,
                            ]}
                            disabled={disableInputs}
                        />
                        <BSInput
                            id="grossSqFt"
                            label="Square Feet"
                            placeholder="Enter square feet"
                            value={data?.grossSqFt ?? ""}
                            error={errors?.grossSqFt ?? null}
                            errorText={"Field must be a positive number"}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            extraFormControlStyles={{ flex: "1 1 0" }}
                            disabled={disableInputs}
                        />
                    </StyledFlexItemRow>

                    <StyledFlexItemRow>
                        <BSInput
                            id="numberOfBuildings"
                            label="Number of Buildings"
                            placeholder="Enter number of buildings"
                            value={data?.numberOfBuildings ?? ""}
                            error={errors?.numberOfBuildings ?? null}
                            errorText={"Field must be a positive number"}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={disableInputs}
                        />
                        <BSInput
                            id="numberOfTenants"
                            label="Number of Units"
                            placeholder="Enter number of units"
                            value={data?.numberOfTenants ?? ""}
                            error={errors?.numberOfTenants ?? null}
                            errorText={"Field must be a positive number"}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={disableInputs}
                        />
                    </StyledFlexItemRow>

                    <StyledFlexItemRow>
                        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 0" }}>
                            <InputLabel id="select-multiple-client-type">Client Type</InputLabel>
                            <FormControl>
                                <Select
                                    labeId="select-multiple-client-type"
                                    id="clientType"
                                    multiple
                                    value={data?.clientType?.split(",") ?? []}
                                    error={errors?.clientType ?? null}
                                    onChange={(e) => {
                                        handleInput(
                                            "clientType",
                                            isEmptyStr(e.target.value.toString()) ? null : e.target.value.toString()
                                        );
                                    }}
                                    className={StyledLabelSpacing}
                                    disabled={disableInputs}
                                    disableUnderline
                                    sx={{
                                        "& .MuiSelect-select": { padding: 0 },
                                        height: "40px",
                                    }}
                                    renderValue={(selected) => {
                                        return (
                                            <StyledTypeChipContainer style={{ padding: "0" }}>
                                                {selected.map((value) => {
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={value}
                                                            onDelete={() => {
                                                                removeChip("clientType", selected, value);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </StyledTypeChipContainer>
                                        );
                                    }}
                                >
                                    {clientTypes.map((clientType) => (
                                        <MenuItem key={clientType.label} value={clientType.value}>
                                            {clientType.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </StyledFlexItemRow>
                </div>

                <div style={{ flex: "4 4 0" }}>
                    <StyledFlexItemRow>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!data.institutionalClientFlg}
                                    onChange={(e, checked) => handleInput(e.target.value, checked)}
                                    value="institutionalClientFlg"
                                    disabled={disableInputs}
                                    color="primary"
                                />
                            }
                            label="Institutional Client"
                        />
                        <BSInput
                            id="trueClientName"
                            label="True Client Name"
                            placeholder='Enter "true" client name'
                            value={data?.trueClientName ?? ""}
                            error={errors?.trueClientName ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={disableInputs}
                        />
                    </StyledFlexItemRow>

                    <BSInput
                        id="propertyOwnerEntity"
                        label="Property Owner's Legal Entity / W-9 Entity"
                        placeholder="Enter legal entity / W-9 entity name"
                        value={isSnapshot ? data?.propertyOwnerLegalEntity ?? null : data?.propertyOwnerEntity ?? null}
                        error={errors?.propertyOwnerEntity ?? null}
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        disabled={disableInputs}
                    />
                    <BSInput
                        id="w9TaxClassification"
                        label="W-9 Federal Tax Classification"
                        placeholder="Enter W-9 federal tax classification"
                        value={data?.w9TaxClassification ?? ""}
                        error={errors?.w9TaxClassification ?? null}
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        disabled={disableInputs}
                    />

                    <StyledFlexItemRow>
                        <BSInput
                            id="w9TaxId"
                            label="W-9 Tax ID"
                            placeholder="Enter W-9 Tax ID"
                            value={data?.w9TaxId ?? null}
                            error={errors?.w9TaxId ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={selectedTaxIdType !== "W9" || disableInputs}
                            checkbox={true}
                            checkboxVal={"W9"}
                            disableCheckbox={disableInputs}
                            onChangeCheckbox={handleTaxIdSelection}
                            checked={selectedTaxIdType === "W9"}
                        />
                        <BSInput
                            id="w9Ssn"
                            label="SSN Tax ID"
                            placeholder="Enter SSN Tax ID"
                            value={data?.w9Ssn ?? null}
                            error={errors?.w9Ssn ?? null}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            disabled={selectedTaxIdType !== "SSN" || disableInputs}
                            checkbox={true}
                            checkboxVal={"SSN"}
                            disableCheckbox={disableInputs}
                            onChangeCheckbox={handleTaxIdSelection}
                            checked={selectedTaxIdType === "SSN"}
                        />
                    </StyledFlexItemRow>

                    <BSSelect
                        id="managementType"
                        label="Scope of Work"
                        placeholder="Select scope of work"
                        value={data?.managementType ?? ""}
                        error={errors?.managementType ?? null}
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        options={[
                            {
                                value: "",
                                label: "Select",
                                disabled: true,
                            },
                            ...propertyManagementTypes,
                        ]}
                        disabled={disableInputs}
                    />

                    <StyledFlexItemRow>
                        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 0" }}>
                            <InputLabel id="select-multiple-asset-type">Asset Type</InputLabel>
                            <FormControl fullwidth>
                                <Select
                                    labeId="select-multiple-asset-type"
                                    id="assetType"
                                    value={data.assetType?.split(",") ?? []}
                                    error={errors?.assetType ?? null}
                                    onChange={(e) => {
                                        handleInput(
                                            "assetType",
                                            isEmptyStr(e.target.value.toString()) ? null : e.target.value.toString()
                                        );
                                    }}
                                    className={StyledLabelSpacing}
                                    disabled={disableInputs}
                                    disableUnderline
                                    sx={{
                                        "& .MuiSelect-select": { padding: 0 },
                                        height: "40px",
                                    }}
                                    renderValue={(selected) => {
                                        return (
                                            <StyledTypeChipContainer>
                                                <Chip
                                                    key={selected}
                                                    label={selected}
                                                    onDelete={() => {
                                                        removeChip("assetType", selected, selected.toString());
                                                    }}
                                                />
                                            </StyledTypeChipContainer>
                                        );
                                    }}
                                >
                                    {assetTypes.map((assetType) => (
                                        <MenuItem key={assetType.label} value={assetType.value}>
                                            {assetType.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </StyledFlexItemRow>
                </div>

                {hasPortfolioProperties && (
                    <div style={{ flex: "1 1 0" }}>
                        <StyledPortfolioPropertyContainer>
                            {(data?.assetPortfolioProperties ?? []).map((property) => (
                                <StyledPortfolioPropertyLinkContainer>
                                    <StyledPortfolioPropertyLink
                                        to={`/propdetail/${property.propertyKey}`}
                                        target="_blank"
                                    >
                                        {property.primaryAddress.address1}
                                        <br />
                                        <StyledPortfolioPropertyAddress>
                                            {property.primaryAddress.city.city},{" "}
                                            {property.primaryAddress.city.state.stateCode}{" "}
                                            {property.primaryAddress.zipCode.zipCode}
                                        </StyledPortfolioPropertyAddress>
                                    </StyledPortfolioPropertyLink>
                                </StyledPortfolioPropertyLinkContainer>
                            ))}
                        </StyledPortfolioPropertyContainer>
                    </div>
                )}
            </StyledMasterPropertyContainer>
        </React.Fragment>
    );
};

export default MasterPropertyForm;
